import React from 'react';
import { useTheme, Typography, Stack, Box, Card } from '@mui/material';
import { AddAlert, Adjust, Medication } from '@mui/icons-material';
import dayjs from 'op-utils/dayjs';
import { Badge } from 'shared-components/components';
import { AlertCardProps, AllergyCardProps, DiagnosisCardProps, Alert, Allergy, Diagnosis } from './types';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import ScribeCard from './ScribeCard';

const AlertCard = (props: AlertCardProps): JSX.Element => {
  const { index, info } = props;
  return (
    <Stack direction="row" alignItems="flex-start" gap={1} sx={{ width: '100%' }}>
      <Typography variant="caption">{index}.</Typography>
      <Stack direction="column" sx={{ width: '100%' }}>
        <Typography variant="caption">{info.name}:</Typography>
        <Typography variant="caption">
          Status: {info.status} | Assigned:{' '}
          {info.assigned && dayjs.utc(info.assigned).format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat)}
        </Typography>
        <Typography variant="caption">Note: {info.comment}</Typography>
      </Stack>
    </Stack>
  );
};

const AllergyCard = (props: AllergyCardProps): JSX.Element => {
  const { index, info } = props;
  return (
    <Stack direction="row" alignItems="flex-start" gap={1} sx={{ width: '100%' }}>
      <Typography variant="caption">{index}.</Typography>
      <Stack direction="column" sx={{ width: '100%' }}>
        <Typography variant="caption">{info.name}:</Typography>
        <Typography variant="caption">{`${info.severity} - ${info.reaction}`}</Typography>
      </Stack>
    </Stack>
  );
};

const DiagnosisCard = (props: DiagnosisCardProps): JSX.Element => {
  const { index, info } = props;
  return (
    info.diagnosisCode && (
      <Stack key={index} direction="row" alignItems="flex-start" gap={1} sx={{ width: '100%' }}>
        <Typography variant="caption">{index}.</Typography>
        <Stack sx={{ width: '100%' }}>
          <Typography variant="caption">
            {info.diagnosisCode.diagnosisCode} {info.isPrimaryDiagnosis ? 'Primary' : 'Metastasis'}
          </Typography>
          <Typography variant="caption">
            {info.diagnosisCode.tumourStream
              ? `${info.diagnosisCode.tumourStream.name} - ${info.diagnosisCode.diagnosisDescription}`
              : info.diagnosisCode.diagnosisDescription || '-'}
          </Typography>
        </Stack>
      </Stack>
    )
  );
};

interface ROPatientDetailCardsProps {
  filteredDiagnosis: Diagnosis[];
  filteredAllergies: Allergy[];
  allergyCardProps: { bubbleColor: string; bubbleText: string | number };
  alerts: Alert[];
  scribeEnabled: boolean;
}

const ROPatientDetailCards: React.FC<ROPatientDetailCardsProps> = ({
  filteredDiagnosis,
  filteredAllergies,
  allergyCardProps,
  alerts,
  scribeEnabled,
}) => {
  const theme = useTheme();

  return (
    <>
      <Card
        variant="outlined"
        elevation={0}
        sx={{
          overflow: 'auto',
          flexBasis: '33%',
          boxSizing: 'border-box',
          borderRadius: '8px',
          minWidth: '31%',
          width: 'fit-content',
          padding: '0 8px 8px',
          height: '97px',
          marginLeft: '8px',
        }}>
        <Stack
          data-testid="diagnosis-card-header"
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            width: '100%',
            position: 'sticky',
            marginBottom: '4px',
            top: 0,
            paddingTop: '8px',
            left: 0,
            bgcolor: 'background.paper',
            fontSize: '14px',
            lineHeight: '20px',
          }}>
          <Adjust color="primary" />
          <Typography variant="body2">Diagnosis</Typography>
          {filteredDiagnosis && filteredDiagnosis?.length > 0 && (
            <Badge $bgColor={theme.palette.primary.main} sx={{ marginLeft: 'auto' }}>
              {filteredDiagnosis.length}
            </Badge>
          )}
        </Stack>
        <Stack direction="column" gap={1} sx={{ width: '100%', paddingLeft: '8px' }}>
          {filteredDiagnosis && filteredDiagnosis?.length ? (
            filteredDiagnosis.map((diagnosisInfo: any, index: number) => {
              return (
                <DiagnosisCard index={index + 1} info={diagnosisInfo} key={diagnosisInfo.diagnosisCode.diagnosisCode} />
              );
            })
          ) : (
            <Typography variant="caption" color="text.primary">
              No diagnoses available
            </Typography>
          )}
        </Stack>
      </Card>
      <Card
        variant="outlined"
        elevation={0}
        sx={{
          overflow: 'auto',
          flexBasis: '33%',
          boxSizing: 'border-box',
          borderRadius: '8px',
          minWidth: '31%',
          width: 'fit-content',
          padding: '0 8px 8px',
          height: '97px',
          marginLeft: '8px',
        }}>
        <Stack
          data-testid="allergies-card-header"
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            width: '100%',
            position: 'sticky',
            marginBottom: '4px',
            top: 0,
            paddingTop: '8px',
            left: 0,
            bgcolor: 'background.paper',
            fontSize: '14px',
            lineHeight: '20px',
          }}>
          <Medication color="primary" />
          <Typography variant="body2">{scribeEnabled ? 'Allergies & Alerts' : 'Allergies'}</Typography>
          {allergyCardProps.bubbleText && (
            <Badge $bgColor={theme.palette.primary.main} sx={{ marginLeft: 'auto' }}>
              {allergyCardProps.bubbleText}
            </Badge>
          )}
        </Stack>
        <Box sx={{ fontWeight: 'bold', fontSize: '14px', lineHeight: '20px' }}>
          {filteredAllergies && filteredAllergies.length ? (
            filteredAllergies.map((allergyInfo: any, index: number) => {
              return <AllergyCard index={index + 1} info={allergyInfo} key={allergyInfo.name} />;
            })
          ) : (
            <Typography variant="caption" color="text.primary">
              {scribeEnabled ? 'None available' : 'No allergies available'}
            </Typography>
          )}
        </Box>
      </Card>
      {scribeEnabled ? (
        <ScribeCard />
      ) : (
        <Card
          variant="outlined"
          elevation={0}
          sx={{
            overflow: 'auto',
            flexBasis: '33%',
            boxSizing: 'border-box',
            borderRadius: '8px',
            minWidth: '31%',
            width: 'fit-content',
            padding: '0 8px 8px',
            height: '97px',
            marginLeft: '8px',
          }}>
          <Stack
            data-testid="alerts-card-header"
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
              width: '100%',
              position: 'sticky',
              marginBottom: '4px',
              top: 0,
              paddingTop: '8px',
              left: 0,
              bgcolor: 'background.paper',
              fontSize: '14px',
              lineHeight: '20px',
            }}>
            <AddAlert color="primary" />
            <Typography variant="body2">Alerts</Typography>
            {alerts && alerts.length > 0 && (
              <Badge $bgColor={theme.palette.primary.main} sx={{ marginLeft: 'auto' }}>
                {alerts.length}
              </Badge>
            )}
          </Stack>
          <Box sx={{ fontWeight: 'bold', fontSize: '14px', lineHeight: '20px' }}>
            {alerts && alerts.length ? (
              alerts.map((alertInfo: any, index: number) => {
                return <AlertCard index={index + 1} info={alertInfo} key={`${alertInfo.name}-${index}`} />;
              })
            ) : (
              <Typography variant="caption" color="text.primary">
                No alerts available
              </Typography>
            )}
          </Box>
        </Card>
      )}
    </>
  );
};

export default ROPatientDetailCards;
